import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const UPSELL_SECTION_ID = 'cart-upsell';
const UPSELL_FALLBACK_SECTION_ID = 'cart-upsell-fallback';
const UPSELL_LIMIT = 10;
// Complementary products are the "frequently bought together" set curated in
// Search & Discovery. Fall back to algorithmic recommendations, and finally to
// a fixed collection, so the drawer never shows an empty gap.
const UPSELL_INTENTS = ['complementary', 'related'];
const UPSELL_SETTLE_MS = 250;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #upsellObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #upsellTimer;

  /** @type {boolean} */
  #upsellPending = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#startUpsellObserver();
    this.#scheduleUpsell();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#upsellObserver?.disconnect();
    this.#upsellObserver = null;
    clearTimeout(this.#upsellTimer);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    this.#scheduleUpsell();
  };

  #handleDialogOpen = () => {
    this.#scheduleUpsell();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Returns the drawer dialog element, if it is present.
   * @returns {Element | null}
   */
  #getDialog() {
    return this.querySelector('dialog');
  }

  /**
   * The drawer re-renders itself on every cart change. Wait for that to settle
   * before touching the DOM, so the upsell never fights the theme's morph pass.
   */
  #scheduleUpsell() {
    clearTimeout(this.#upsellTimer);
    this.#upsellTimer = setTimeout(() => this.#renderUpsell(), UPSELL_SETTLE_MS);
  }

  #startUpsellObserver() {
    const dialog = this.#getDialog();
    if (!dialog || this.#upsellObserver) return;

    this.#upsellObserver = new MutationObserver(() => this.#scheduleUpsell());
    this.#upsellObserver.observe(dialog, { childList: true, subtree: true });
  }

  /**
   * Requests a rendered upsell section and returns it only when it has cards.
   *
   * @param {string} url - The section rendering URL to request.
   * @returns {Promise<Element | null>}
   */
  async #fetchUpsellMarkup(url) {
    const response = await fetch(url);
    if (!response.ok) return null;

    const markup = await response.text();
    const upsell = new DOMParser().parseFromString(markup, 'text/html').querySelector('.cart-upsell');

    if (!upsell || upsell.querySelectorAll('.cart-upsell__card').length === 0) return null;

    return upsell;
  }

  /**
   * Walks the recommendation tiers and returns the first one that has products.
   *
   * @param {string} root - The shop's root URL.
   * @param {number | string} productId - The product to base recommendations on.
   * @returns {Promise<Element | null>}
   */
  async #resolveUpsell(root, productId) {
    for (const intent of UPSELL_INTENTS) {
      const params = new URLSearchParams({
        section_id: UPSELL_SECTION_ID,
        product_id: String(productId),
        limit: String(UPSELL_LIMIT),
        intent,
      });

      const upsell = await this.#fetchUpsellMarkup(root + 'recommendations/products?' + params.toString());
      if (upsell) return upsell;
    }

    // Nothing is configured for this product, so offer the fallback collection.
    return this.#fetchUpsellMarkup(root + '?section_id=' + UPSELL_FALLBACK_SECTION_ID);
  }

  /**
   * Fetches recommendations for the most recently added cart item and places them
   * between the line items and the cart summary. Reuses the existing markup when
   * the cart has not changed so a re-render never stacks duplicates.
   */
  async #renderUpsell() {
    if (this.#upsellPending) return;

    const dialog = this.#getDialog();
    if (!dialog) return;

    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!summary || !summary.parentElement) return;

    this.#upsellPending = true;

    try {
      const root = window.Shopify?.routes?.root || '/';

      const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      // Shopify returns the most recently added line first, which is what the
      // shopper just chose - so recommendations follow their latest intent.
      const anchor = cart?.items?.[0];

      if (!anchor) {
        dialog.querySelectorAll('.cart-upsell-slot').forEach((node) => node.remove());
        return;
      }

      const cartKey = cart.items.map((item) => item.variant_id + 'x' + item.quantity).join(',');
      const existing = /** @type {HTMLElement | null} */ (dialog.querySelector('.cart-upsell-slot'));
      const isCurrent =
        existing?.dataset.cartKey === cartKey &&
        existing?.nextElementSibling === summary &&
        Boolean(existing?.querySelector('.cart-upsell')) &&
        dialog.querySelectorAll('.cart-upsell-slot').length === 1;

      // Nothing changed, so leave the drawer markup completely alone.
      if (isCurrent) return;

      const upsell = await this.#resolveUpsell(root, anchor.product_id);

      const slot = existing ?? document.createElement('div');
      slot.className = 'cart-upsell-slot';
      slot.dataset.cartKey = cartKey;

      if (upsell) {
        slot.replaceChildren(upsell);
      } else {
        slot.replaceChildren();
      }

      if (slot.nextElementSibling !== summary) {
        summary.parentElement.insertBefore(slot, summary);
      }

      // Drop any stragglers a previous re-render may have left behind.
      dialog.querySelectorAll('.cart-upsell-slot').forEach((node) => {
        if (node !== slot) node.remove();
      });
    } catch (error) {
      // The drawer must keep working even if recommendations fail.
    } finally {
      this.#upsellPending = false;
      this.#updateStickyState();
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}